import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "progressBar", "progressPercentage", "progressStatus", "processedCount", "totalCount", "eta", "progressMessage", "successfulCount", "failedCount", "inlineProgress", "inlineProcessedCount", "inlineTotalCount", "inlineProgressPercentage", "inlineSuccessfulCount", "inlineFailedCount"]
  static values = { 
    progressId: String,
    progressUrl: String
  }

  connect() {
    if (this.hasProgressIdValue) {
      this.showModal()
      this.startProgressTracking()
    }
  }

  disconnect() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval)
    }
  }

  showModal() {
    const modal = new bootstrap.Modal(this.modalTarget)
    modal.show()
  }

  startProgressTracking() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval)
    }

    // Start polling for progress updates
    this.progressInterval = setInterval(() => {
      this.fetchProgress()
    }, 2000) // Poll every 2 seconds

    // Initial fetch
    this.fetchProgress()
  }

  async fetchProgress() {
    try {
      let response = await fetch(`${this.progressUrlValue}?progress_id=${this.progressIdValue}`)
      
      if (!response.ok) {
        if (response.status === 404) {
          this.handleProgressNotFound()
        }
        return
      }

      const data = await response.json()
      this.updateProgressUI(data)
      
      // Stop polling if completed or failed
      if (data.status === 'completed' || data.status === 'failed') {
        this.stopProgressTracking()
        
        // Auto-close modal after 3 seconds if completed
        if (false && data.status === 'completed') {
          setTimeout(() => {
            this.closeModal()
          }, 3000)
        }
      }
    } catch (error) {
      console.error('Error fetching progress:', error)
    }
  }

  updateProgressUI(data) {
    // Update modal progress bar
    this.progressBarTarget.style.width = `${data.progress_percentage}%`
    this.progressPercentageTarget.textContent = `${data.progress_percentage}%`

    // Update modal counts
    this.processedCountTarget.textContent = data.processed_locations || 0
    this.totalCountTarget.textContent = data.total_locations || 0
    this.successfulCountTarget.textContent = data.successful_locations || 0
    this.failedCountTarget.textContent = data.failed_locations || 0

    // Update status badge
    const statusClass = this.getStatusClass(data.status)
    const statusText = this.getStatusText(data.status)
    
    this.progressStatusTarget.className = `badge ${statusClass}`
    this.progressStatusTarget.textContent = statusText

    // Update ETA
    this.etaTarget.textContent = this.getETAText(data)

    // Update message
    this.updateProgressMessage(data)

    // Update inline progress if it exists
    this.updateInlineProgress(data)
  }

  getStatusClass(status) {
    switch(status) {
      case 'pending': return 'bg-warning'
      case 'in_progress': return 'bg-primary'
      case 'completed': return 'bg-success'
      case 'failed': return 'bg-danger'
      default: return 'bg-info'
    }
  }

  getStatusText(status) {
    switch(status) {
      case 'pending': return 'Pending'
      case 'in_progress': return 'In Progress'
      case 'completed': return 'Completed'
      case 'failed': return 'Failed'
      default: return 'Initializing...'
    }
  }

  getETAText(data) {
    if (data.estimated_time_remaining && data.status === 'in_progress') {
      return this.formatTime(data.estimated_time_remaining)
    } else if (data.status === 'completed') {
      return 'Completed'
    } else if (data.status === 'failed') {
      return 'Failed'
    } else {
      return 'Calculating...'
    }
  }

  updateProgressMessage(data) {
    const messageConfig = this.getMessageConfig(data)
    
    this.progressMessageTarget.innerHTML = `
      <div class="alert ${messageConfig.class} p-2">
        <i class="${messageConfig.icon} me-2 mt-1 ms-2"></i> ${messageConfig.text}
      </div>
    `
  }

  updateInlineProgress(data) {
    // Update inline progress if it exists
    if (this.hasInlineProgressTarget) {
      // Update inline progress bar
      const inlineProgressBar = this.inlineProgressTarget.querySelector('.progress-bar')
      if (inlineProgressBar) {
        inlineProgressBar.style.width = `${data.progress_percentage}%`
      }

      // Update inline counts
      if (this.hasInlineProcessedCountTarget) {
        this.inlineProcessedCountTarget.textContent = data.processed_locations || 0
      }
      if (this.hasInlineTotalCountTarget) {
        this.inlineTotalCountTarget.textContent = data.total_locations || 0
      }
      if (this.hasInlineProgressPercentageTarget) {
        this.inlineProgressPercentageTarget.textContent = data.progress_percentage || 0
      }
      if (this.hasInlineSuccessfulCountTarget) {
        this.inlineSuccessfulCountTarget.textContent = data.successful_locations || 0
      }
      if (this.hasInlineFailedCountTarget) {
        this.inlineFailedCountTarget.textContent = data.failed_locations || 0
      }

      // Update inline progress alert class based on status
      const alertClass = this.getInlineAlertClass(data.status)
      this.inlineProgressTarget.className = `alert ${alertClass} mt-3`
    }
  }

  getInlineAlertClass(status) {
    switch(status) {
      case 'pending': return 'alert-warning'
      case 'in_progress': return 'alert-info'
      case 'completed': return 'alert-success'
      case 'failed': return 'alert-danger'
      default: return 'alert-info'
    }
  }

  getMessageConfig(data) {
    if (data.status === 'completed') {
      return {
        class: 'alert-success',
        icon: 'fas fa-check-circle',
        text: 'EmailRx buttons configuration setup completed successfully!'
      }
    } else if (data.status === 'failed') {
      return {
        class: 'alert-danger',
        icon: 'fas fa-exclamation-triangle',
        text: `EmailRx buttons configuration failed: ${data.error_message || 'Unknown error'}`
      }
    } else if (data.status === 'in_progress') {
      return {
        class: 'alert-primary',
        icon: 'fas fa-spinner fa-spin',
        text: `Processing location ${data.processed_locations || 0} of ${data.total_locations || 0}`
      }
    } else {
      return {
        class: 'alert-info',
        icon: 'fas fa-info-circle',
        text: 'Configuring EmailRx buttons configuration for selected sub-accounts...'
      }
    }
  }

  formatTime(seconds) {
    if (seconds < 60) {
      return Math.round(seconds) + 's'
    } else if (seconds < 3600) {
      return Math.round(seconds / 60) + 'm'
    } else {
      return Math.round(seconds / 3600) + 'h ' + Math.round((seconds % 3600) / 60) + 'm'
    }
  }

  handleProgressNotFound() {
    this.stopProgressTracking()
    this.updateProgressUI({ 
      status: 'failed', 
      error_message: 'Progress record not found' 
    })
  }

  stopProgressTracking() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval)
      this.progressInterval = null
    }
  }

  closeModal() {
    const modal = bootstrap.Modal.getInstance(this.modalTarget)
    if (modal) {
      modal.hide()
    }
  }

  refresh() {
    if (this.hasProgressIdValue) {
      this.fetchProgress()
    }
  }
} 